/* Своё фото и своё видео того, кто говорит (APP-SPEC-4 §3.4–§3.6): рамка загрузки BotCore.upload на месте главной рамки.
   Пусто — «Загрузите фото с лицом» / «Загрузите видео с лицом», подпись, «Загрузить» и «Снять», строка ограничений поля;
   с файлом — превью в форме результата (у кружка круглое), «Заменить · Кадр · Убрать», у видео — длительность и фрагмент.
   Бейдж цены за единицу — в правом верхнем углу рамки. Файл уходит на сервер только после «Права на материалы». */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import { StudioIcon } from '../StudioIcon';
import { StudioSheet } from '../StudioSheet';
import { StudioUploadRights } from '../StudioUploadRights';
import { uploadLimits } from '../StudioUploadExample';
import type { StudioForm } from '../studio-api';
import { useVideoSourcePreview } from '../../video/use-video-source-preview';
import type { CircleFormState } from './CircleFieldInput';
import { mmss } from './order-tail-model';
import { ToolIcon } from './ToolIcon';
import type { CircleCrop } from './circle-model';

export interface CircleMedia {
  url: string;
  kind: 'photo' | 'video';
  width: number;
  height: number;
  durationMs: number;
}
export function CircleUpload({
  field,
  state,
  round,
  ratio,
  crop,
  canCrop,
  badge,
  onCrop,
  onMedia,
  onCleared,
  children,
}: {
  field: StudioForm['fields'][number];
  state: CircleFormState;
  /** Кружок: превью круглое */
  round: boolean;
  /** Пропорции превью у видео-формата (ширина / высота) */
  ratio: number;
  crop: CircleCrop | null;
  canCrop: boolean;
  badge?: ReactNode;
  onCrop(media: CircleMedia): void;
  onMedia?(media: CircleMedia | null): void;
  onCleared?(): void;
  /** Под рамкой: фрагмент ролика */
  children?: ReactNode;
}) {
  const isVideo = field.upload?.kind === 'video';
  const maximum = field.upload?.max_bytes ?? (isVideo ? 200 : 10) * 1024 * 1024;
  const preview = useVideoSourcePreview(state.inputs[field.key], state.ownerId, isVideo ? 'video' : 'photo', maximum);
  const [error, setError] = useState('');
  const [consentFile, setConsentFile] = useState<File | null>(null);
  const [meta, setMeta] = useState<{ width: number; height: number; durationMs: number } | null>(null);
  const report = useRef(onMedia);
  useEffect(() => {
    report.current = onMedia;
  });
  const url = preview.url;
  useEffect(() => {
    report.current?.(url && meta ? { url, kind: isVideo ? 'video' : 'photo', ...meta } : null);
  }, [url, meta, isVideo]);
  if (!field.upload) return null;
  const select = (file: File | undefined) => {
    if (!file || state.locked) return;
    setError('');
    if (file.size > maximum) {
      setError(uiFormat('Файл больше {0} МБ', Math.round(maximum / 1024 / 1024)));
      return;
    }
    if (field.upload!.accept.length && !field.upload!.accept.includes(file.type)) {
      setError(isVideo ? 'Нужен ролик MP4 или MOV' : 'Нужно фото JPG или PNG');
      return;
    }
    if (!state.rights) {
      setConsentFile(file);
      return;
    }
    if (state.uploadIds[field.key]) state.clearUpload(field.key);
    setMeta(null);
    preview.select(file);
    void state.upload(field.key, file);
  };
  const clear = () => {
    state.clearUpload(field.key);
    preview.select(null);
    setMeta(null);
    onCleared?.();
  };
  const has = !!state.uploadIds[field.key] || !!state.inputs[field.key];
  const pending = !!state.uploadIds[field.key] && !state.inputs[field.key];
  const picker = (label: string, capture: boolean, className: string, icon: string) => (
    <label className={`bcu-pl ${className}`}>
      <StudioIcon kind={icon} />
      <span>{uiText(label)}</span>
      <input
        type="file"
        className="studio-native-file-input"
        aria-label={uiText(capture ? (isVideo ? 'Снять видео' : 'Снять фото') : label === 'Заменить' ? 'Заменить файл' : isVideo ? 'Загрузить видео' : 'Фото с лицом')}
        accept={capture ? (isVideo ? 'video/*' : 'image/*') : field.upload!.accept.join(',')}
        {...(capture ? { capture: 'user' as const } : {})}
        disabled={state.locked}
        onChange={event => {
          select(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </label>
  );
  const cropStyle = crop && !isVideo ? { objectPosition: `${(crop.cx * 100).toFixed(1)}% ${(crop.cy * 100).toFixed(1)}%` } : undefined;
  return (
    <div className={`pf-up tc-up circle-up${round && !isVideo ? ' round' : ''}`}>
      <div className={`bcu${has ? ' has' : ''}`} data-bcu={field.key}>
        {!has ? (
          <div className="bcu-st" data-k="empty">
            <div className="bcu-e">
              <span className="bcu-ic">
                <ToolIcon kind="upload" size={22} />
              </span>
              <b className="bcu-t">{uiText(isVideo ? 'Загрузите видео с лицом' : 'Загрузите фото с лицом')}</b>
              <span className="bcu-s">{uiText(isVideo ? 'Лицо в кадре, из длинного выберете минуту' : 'Лицо крупно и смотрит в камеру')}</span>
              <div className="bcu-acts">
                {picker('Загрузить', false, 'pri', 'upload')}
                {picker('Снять', true, '', 'camera')}
              </div>
              <small className="bcu-n">{uploadLimits(field)}</small>
            </div>
          </div>
        ) : (
          <div className="bcu-st" data-k="file">
            <div className="bcu-f dk">
              {url && !isVideo && <img className="bcu-bg" src={url} alt="" aria-hidden="true" />}
              <div className="bcu-pa">
                <span
                  className="bcu-pv"
                  style={{ aspectRatio: round && !isVideo ? '1 / 1' : String(ratio || 9 / 16), ['--bcu-r' as string]: (round ? 1 : ratio).toFixed(4) }}
                >
                  {preview.loading && <span role="status">{uiText('Загружаем…')}</span>}
                  {url &&
                    (isVideo ? (
                      <video
                        src={url}
                        muted
                        playsInline
                        preload="metadata"
                        aria-label={uiText('Своё видео')}
                        onLoadedMetadata={event =>
                          setMeta({
                            width: event.currentTarget.videoWidth,
                            height: event.currentTarget.videoHeight,
                            durationMs: Math.floor(event.currentTarget.duration * 1000) || 0,
                          })
                        }
                      />
                    ) : (
                      <img
                        src={url}
                        alt={uiText('Своё фото')}
                        style={cropStyle}
                        onLoad={event => setMeta({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight, durationMs: 0 })}
                      />
                    ))}
                  {isVideo && meta?.durationMs ? (
                    <span className="bcu-dur">
                      <StudioIcon kind="play" />
                      <b className="mono">{mmss(meta.durationMs / 1000)}</b>
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="bcu-fa">
                {picker('Заменить', false, 'gl', 'upload')}
                {canCrop && url && meta && (
                  <button
                    type="button"
                    className="bcu-pl gl"
                    disabled={state.locked}
                    onClick={() => onCrop({ url, kind: isVideo ? 'video' : 'photo', ...meta })}
                  >
                    <StudioIcon kind="image" />
                    <span>{uiText('Кадр')}</span>
                  </button>
                )}
                <button type="button" className="bcu-pl gl" disabled={state.locked} onClick={clear}>
                  <ToolIcon kind="trash" />
                  <span>{uiText('Убрать')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {badge && <span className="bc-pb pb-corner bcu-pb">{badge}</span>}
      </div>
      {preview.error && (
        <p role="alert">
          {uiText('Не удалось прочитать файл.')}{' '}
          <button type="button" onClick={preview.retry}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {pending && (
        <button type="button" className="ap-qt" disabled={state.busy || state.locked || !state.rights} onClick={() => void state.upload(field.key, null)}>
          {uiText('Продолжить загрузку')}
        </button>
      )}
      {error && <p role="alert">{uiText(error)}</p>}
      {children}
      {consentFile && (
        <StudioSheet title="Права на материалы" onClose={() => setConsentFile(null)}>
          <h2>{uiText('Права на материалы')}</h2>
          <StudioUploadRights state={state} />
          <button
            type="button"
            className="btn"
            disabled={state.locked || !state.rights}
            onClick={() => {
              const file = consentFile;
              setConsentFile(null);
              select(file);
            }}
          >
            {uiText('Продолжить загрузку')}
          </button>
        </StudioSheet>
      )}
    </div>
  );
}
